import React from 'react';
import { Box, Link } from '@mui/material';
import { brand } from '../../theme';
import { pluralize } from '../../utils/format';
import { QUEUE_FILTERS, QueueFilter } from './editorProjectModel';

interface QueueSummaryProps {
  counts: Record<QueueFilter, number>;
  /** The filter applied to the list, if any. */
  active: QueueFilter | null;
  /** Open projects in the list (for the line when nothing needs the editor). */
  openCount: number;
  onChange: (filter: QueueFilter | null) => void;
}

/**
 * One line on what needs the editor: "1 awaiting response · 1 changes
 * requested · 2 due this week". Each part filters the list; choosing the
 * active part again shows everything.
 */
const QueueSummary: React.FC<QueueSummaryProps> = ({ counts, active, openCount, onChange }) => {
  // A part stays visible while it is the active filter, even at zero, so it
  // can be switched off again.
  const parts = QUEUE_FILTERS.filter(({ value }) => counts[value] > 0 || value === active);

  if (parts.length === 0) {
    return (
      <Box component="span" data-testid="queue-summary">
        {openCount > 0
          ? `${pluralize(openCount, 'active project')} · nothing needs your response`
          : 'No active projects'}
      </Box>
    );
  }

  return (
    <Box
      component="span"
      role="group"
      aria-label="Show only"
      data-testid="queue-summary"
      sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1, rowGap: 0.25 }}
    >
      {parts.map(({ value, label }, index) => {
        const selected = active === value;
        return (
          <React.Fragment key={value}>
            {/* Spaced in the text too, so copied text reads as a sentence. */}
            {index > 0 && (
              <Box component="span" aria-hidden="true">
                {' · '}
              </Box>
            )}
            <Link
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(selected ? null : value)}
              underline="always"
              sx={{
                font: 'inherit',
                verticalAlign: 'baseline',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: selected ? 700 : 600,
                color: selected ? brand.copperDark : 'text.primary',
                // A quiet underline says "this filters"; the chosen part is copper.
                textDecorationColor: selected ? brand.copper : brand.borderStrong,
                textDecorationThickness: selected ? '2px' : '1px',
                textUnderlineOffset: '4px',
                '&:hover': { color: brand.copperDark, textDecorationColor: brand.copper },
                '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2, borderRadius: '4px' },
              }}
            >
              {label(counts[value])}
            </Link>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default QueueSummary;
